/**
 * 设备和浏览器检测工具
 * 用于判断当前运行环境的设备类型、操作系统和浏览器类型
 */

/**
 * 获取 User Agent 字符串
 * @returns {string} User Agent
 */
const getUserAgent = () => {
  return navigator.userAgent || navigator.vendor || window.opera || ''
}

const ua = getUserAgent()

/**
 * 判断是否为移动设备
 * @returns {boolean} true 表示移动设备，false 表示桌面设备
 */
export const isMobile = () => {
  // 检测常见的移动设备标识
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
  return mobileRegex.test(ua)
}

/**
 * 判断是否为桌面/PC 设备
 * @returns {boolean} true 表示桌面设备，false 表示移动设备
 */
export const isPC = () => {
  return !isMobile()
}

/**
 * 判断是否为 iPhone
 * @returns {boolean} true 表示 iPhone 设备
 */
export const isIPhone = () => {
  return /iPhone/i.test(ua)
}

/**
 * 判断是否为 iPad
 * @returns {boolean} true 表示 iPad 设备
 */
export const isIPad = () => {
  // iOS 13+ iPad 可能伪装成 Mac，需要通过 touch 支持判断
  return /iPad/i.test(ua) || (
    /Macintosh/i.test(ua) &&
    navigator.maxTouchPoints &&
    navigator.maxTouchPoints > 1
  )
}

/**
 * 判断是否为 Mac 设备
 * @returns {boolean} true 表示 Mac 设备
 */
export const isMac = () => {
  return /Macintosh|Mac OS X/i.test(ua)
}

/**
 * 判断是否为 iOS 设备（iPhone 或 iPad）
 * @returns {boolean} true 表示 iOS 设备
 */
export const isIOS = () => {
  return isIPhone() || isIPad()
}

/**
 * 判断是否为 Apple 设备（iPhone、iPad 或 Mac）
 * @returns {boolean} true 表示 Apple 设备
 */
export const isApple = () => {
  return isIPhone() || isIPad() || isMac()
}

/**
 * 判断是否为 Android 设备
 * @returns {boolean} true 表示 Android 设备
 */
export const isAndroid = () => {
  return /Android/i.test(ua)
}

/**
 * 判断是否为 Safari 浏览器
 * @returns {boolean} true 表示 Safari 浏览器
 */
export const isSafari = () => {
  // Safari 的 UA 包含 Safari 但不包含 Chrome/Chromium
  return /Safari/i.test(ua) && !/Chrome|Chromium|CriOS/i.test(ua)
}

/**
 * 判断是否为 Chrome 浏览器
 * @returns {boolean} true 表示 Chrome 浏览器
 */
export const isChrome = () => {
  // Chrome 的 UA 包含 Chrome 但不包含 Edge
  // iPhone 上的 Chrome 使用 CriOS 标识
  return (/Chrome/i.test(ua) || /CriOS/i.test(ua)) && !/Edge|Edg/i.test(ua)
}

/**
 * 判断是否为 Firefox 浏览器
 * @returns {boolean} true 表示 Firefox 浏览器
 */
export const isFirefox = () => {
  return /Firefox/i.test(ua)
}

/**
 * 判断是否为 Edge 浏览器
 * @returns {boolean} true 表示 Edge 浏览器
 */
export const isEdge = () => {
  return /Edge|Edg/i.test(ua)
}

/**
 * 判断是否为微信内置浏览器
 * @returns {boolean} true 表示微信浏览器
 */
export const isWeChat = () => {
  return /MicroMessenger/i.test(ua)
}

/**
 * 检测是否为 WebView 环境
 * WebView 包括：iOS WebView、Android WebView、微信、QQ 等内置浏览器
 * 用于 OAuth 登录时判断是否支持第三方授权
 * @returns {boolean} true 表示 WebView 环境
 */
export const isWebView = () => {
  return (
    /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua) || // iOS WebView
    /wv|WebView/i.test(ua) || // Android WebView
    /MicroMessenger/i.test(ua) || // 微信
    /QQ\//i.test(ua) // QQ
  )
}

/**
 * 检测是否为 iPhone Safari 浏览器
 * 用于判断是否需要使用 OAuth redirect 模式
 * @returns {boolean} true 表示 iPhone Safari 浏览器
 */
export const isIPhoneSafari = () => {
  // 必须是 iPhone 且包含 Safari，但不是 WebView
  return isIOS() && isSafari() && !isWebView()
}

/**
 * 获取操作系统类型
 * @returns {'iOS'|'Android'|'Windows'|'Mac'|'Linux'|'Unknown'} 操作系统类型
 */
export const getOS = () => {

  if (/iPhone|iPad|iPod/i.test(ua)) {
    return 'iOS'
  }
  if (/Android/i.test(ua)) {
    return 'Android'
  }
  if (/Windows/i.test(ua)) {
    return 'Windows'
  }
  if (/Macintosh|Mac OS X/i.test(ua)) {
    // 排除伪装成 Mac 的 iPad
    if (isIPad()) {
      return 'iOS'
    }
    return 'Mac'
  }
  if (/Linux/i.test(ua)) {
    return 'Linux'
  }

  return 'Unknown'
}

/**
 * 获取浏览器类型
 * @returns {'Safari'|'Chrome'|'Firefox'|'Edge'|'WeChat'|'Unknown'} 浏览器类型
 */
export const getBrowser = () => {
  if (isWeChat()) {
    return 'WeChat'
  }
  if (isEdge()) {
    return 'Edge'
  }
  if (isChrome()) {
    return 'Chrome'
  }
  if (isFirefox()) {
    return 'Firefox'
  }
  if (isSafari()) {
    return 'Safari'
  }

  return 'Unknown'
}

/**
 * 获取 iOS 版本号
 * @returns {string|null} iOS 版本号，例如 '15.0'，非 iOS 设备返回 null
 */
export const getIOSVersion = () => {
  if (!isIOS()) {
    return null
  }

  const match = ua.match(/OS (\d+)_(\d+)_?(\d+)?/)

  if (match) {
    return `${match[1]}.${match[2]}${match[3] ? '.' + match[3] : ''}`
  }

  return null
}

/**
 * 获取 Android 版本号
 * @returns {string|null} Android 版本号，例如 '12'，非 Android 设备返回 null
 */
export const getAndroidVersion = () => {
  if (!isAndroid()) {
    return null
  }

  const match = ua.match(/Android\s+([\d.]+)/)

  if (match) {
    return match[1]
  }

  return null
}

/**
 * 获取设备详细信息
 * @returns {Object} 设备信息对象
 */
export const getDeviceInfo = () => {
  return {
    // 设备类型
    isMobile: isMobile(),
    isPC: isPC(),
    isIPhone: isIPhone(),
    isIPad: isIPad(),
    isIOS: isIOS(),
    isAndroid: isAndroid(),

    // 操作系统
    os: getOS(),
    osVersion: getOS() === 'iOS' ? getIOSVersion() : getAndroidVersion(),

    // 浏览器
    browser: getBrowser(),
    isSafari: isSafari(),
    isChrome: isChrome(),
    isFirefox: isFirefox(),
    isEdge: isEdge(),
    isWeChat: isWeChat(),
    isWebView: isWebView(),
    isIPhoneSafari: isIPhoneSafari(),

    // User Agent
    userAgent: getUserAgent(),

    // 屏幕信息
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,

    // 触摸支持
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    maxTouchPoints: navigator.maxTouchPoints || 0
  }
}

/**
 * 打印设备信息到控制台（调试用）
 */
export const logDeviceInfo = () => {
  const info = getDeviceInfo()
  console.group('📱 Device Information')
  console.table({
    'Device Type': info.isMobile ? 'Mobile' : 'Desktop',
    'OS': `${info.os} ${info.osVersion || ''}`,
    'Browser': info.browser,
    'Screen': `${info.screenWidth}x${info.screenHeight}`,
    'Viewport': `${info.viewportWidth}x${info.viewportHeight}`,
    'Touch': info.touchSupport ? `Yes (${info.maxTouchPoints} points)` : 'No'
  })
  console.log('User Agent:', info.userAgent)
  console.groupEnd()
}

// 默认导出所有方法
export default {
  isMobile,
  isPC,
  isIPhone,
  isIPad,
  isIOS,
  isApple,
  isMac,
  isAndroid,
  isSafari,
  isChrome,
  isFirefox,
  isEdge,
  isWeChat,
  isWebView,
  isIPhoneSafari,
  getOS,
  getBrowser,
  getIOSVersion,
  getAndroidVersion,
  getDeviceInfo,
  logDeviceInfo
}
